/* —————————— 리액트 내장 ——————————— */
import React, { ReactNode, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
/* ——————————— 컴포넌트 ———————————— */
/* ————————————— HOOK ———————————— */
/* ————————————— API ————————————— */
import { login, FunctionRes as loginFunctionRes } from '@/services/core/auth/login';
import { requestCode, FunctionRes as rcFunctionRes } from '@/services/core/auth/requestCode';
import { whaleOAuth, FunctionRes as whaleFunctionRes } from '@/services/core/sso/whaleOAuth';
import { googleOAuth, FunctionRes as googleFunctionRes } from '@/services/core/sso/googleOAuth';
/* ————————————— 모듈 ————————————— */
import publicMethod from '@/utils/publicMethod';
import { idSaveStore, registerDetailStore, verifyStore } from '@/recoil/store/auth';
import { deviceManagementStore } from '@/recoil/store/teacherManagement';
import { loginSessionCountStore } from '@/recoil/store/schedule';
import { deviceInfoStore, ExternalPopupInfoStore, firstAccessStore, firstLoginStore } from '@/recoil/store/common';
import { popupAutoStore, popupErrorStore, popupStore, popupTutorialStore } from '@/recoil/store/popup';
import { userTokenInfoStore, userBaseInfoStore, forceLogoutStore, withdrawInfoStore } from '@/recoil/store/user';
/* ————————— CSS 및 이미지 ————————— */
import onBoarding1 from '@/assets/image/onBoarding/onBoarding1.png';
import onBoarding2 from '@/assets/image/onBoarding/onBoarding2.png';
import onBoarding3 from '@/assets/image/onBoarding/onBoarding3.png';
import onBoarding4 from '@/assets/image/onBoarding/onBoarding4.png';
import onBoarding5 from '@/assets/image/onBoarding/onBoarding5.png';
import { LogicDelayLog, ncpLogUpdate } from '@/services/ncloud/saveAppLog';

/* ———————— 로그인 HOOK  ———————— */
export default () => {


  /* —————————————————————————————————————————— */
  /* ———————————————— 변수 설정  ———————————————— */
  /* —————————————————————————————————————————— */

  // 페이지 이동
  const navigate = useNavigate();
  // 리다이렉트 확인을 위한 파라미터
  const [searchParams, setSearchParams] = useSearchParams();
  // 입력값 설정
  const [inputs, setInputs] = useState<{ user_id: string, user_pw: string, user_pw_hidden: string }>({ user_id: '', user_pw: '', user_pw_hidden: '' });
  // 아이디 초기화 여부
  const [idRefresh, setIdRefresh] = useState(false);
  // 비밀번호 암호화
  const [passwordHidden, setPasswordHidden] = useState(true);
  // 유효성 체크 (border)
  const [idBorderError, setIdBorderError] = useState(false);
  const [pwBorderError, setPwBorderError] = useState(false);
  // 아이디 미입력 상태값
  const [idBlank, setIdBlank] = useState(false);
  // 비밀번호 미입력 상태값
  const [pwBlank, setPwBlank] = useState(false);
  // CAPS LOCK ON 상태값
  const [capsLockOn, setCapsLockOn] = useState(false);
  // 로그인 실패시 상태값
  const [loginFail, setLoginFail] = useState(false);
  // 아이디 저장
  const [idSave, setIdSave] = useState(false);
  // 팝업 state
  const [popup, setPopup] = useRecoilState(popupStore);
  // 2차인증 사용자정보 데이터 ( 팝업과 데이터 송수신 )
  const [verifyInfo, setVerifyInfo] = useRecoilState(verifyStore);
  // 회원가입 state
  const [registerDetail, setRegisterDetail] = useRecoilState(registerDetailStore);
  // 사용자 토큰 정보
  const setUserTokenInfo = useSetRecoilState(userTokenInfoStore);
  // 사용자 기본 정보
  const setUserBaseInfo = useSetRecoilState(userBaseInfoStore);
  // 디바이스 관리
  const setDeviceManagement = useSetRecoilState(deviceManagementStore);
  // 네이티브 정보
  const deviceInfo = useRecoilValue(deviceInfoStore);
  // 아이디 저장
  const [localSaveId, setLocalSaveId] = useRecoilState(idSaveStore);
  // 중복로그인 체크
  const [forceLogout, setForceLogout] = useRecoilState(forceLogoutStore);
  // 로그인 세션 카운트
  const setLoginSessionCount = useSetRecoilState(loginSessionCountStore);  // 로그인 세션 카운트
  // 팝업 자동 실행
  const setPopupAuto = useSetRecoilState(popupAutoStore);
  // 첫 접속 정보
  const [firstAccess, setFirstAccess] = useRecoilState(firstAccessStore);
  // 회원 탈퇴 정보 초기화
  const resetWithdraw = useResetRecoilState(withdrawInfoStore);
  // 회원가입 정보 초기화
  const resetRegisterDetail = useResetRecoilState(registerDetailStore);
  // 온보딩 UI
  const [onBoarding, setOnBoarding] = useState(false);
  // 온보딩 데이터
  const onBoardingData = [
    { img: onBoarding1, text: 'My School\nCustomized Platform', subText: 'Lingschool connects classrooms\naccording to the school timetable!' },
    { img: onBoarding2, text: 'Comfortable\nClassroom Environment', subText: 'Create a comfortable classroom\nenvironment for better focus!' },
    { img: onBoarding3, text: 'Easy, Fast,\nConvenient!', subText: 'Utilize various teaching and learning\nfeatures optimized for digital classes!' },
    { img: onBoarding4, text: 'Blended Learning\nSolution', subText: 'Easily record classes and conduct\nremote lessons with AI-based speaker tracking cameras!' },
    { img: onBoarding5, text: 'Class Materials\nStorage', subText: 'Manage your class materials\nin personal cloud storage!' }
  ];
  // 온보딩 데이터 슬라이드
  const [currentSlide, setCurrentSlide] = useState(0);
  // 팝업 에러 state
  const setPopupError = useSetRecoilState(popupErrorStore);
  // sso redirect (winPC)
  const [externalPopupInfo, setExternalPopupInfo] = useRecoilState(ExternalPopupInfoStore);


  // 온보딩 슬라이드 세팅 값
  const onBoardingSlideSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    appendDots: (dots: ReactNode) => (<div style={{ width: '160px', position: 'absolute', top: '160px', left: '910px', display: 'flex' }}><ul> {dots} </ul></div>),
    dotsClass: 'dots_custom',
    beforeChange: (current: number, next: SetStateAction<number>) => setCurrentSlide(next)
  };



  // 첫번째 로그인 관리 (최초 로그인이 아닌, 유저 로그아웃 후 재로그인 한 경우도 가능)
  const setFirstLogin = useSetRecoilState(firstLoginStore);
  // 팝업 튜토리얼 state
  const setPopupTutorial = useSetRecoilState(popupTutorialStore);
  // 로그인 버튼 활성화/비활성화
  const [loginClickable, setLoginClickable] = useState(true);
  const sliderRef = useRef<any>(null);
  const goPreviousSlide = useCallback(() => sliderRef.current.slickPrev(), []);
  const goNextSlide = useCallback(() => sliderRef.current.slickNext(), []);
  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState(0);





  // 로그인 딜레이 로그
  const delayLogBody: LogicDelayLog = {
    type: 'check',
    device: deviceInfo?.osInfo!,
    code: 'CK_0001',
    description: '서비스 로그인',
    startAt: '',
    endAt: '',
    millisecond: '',
    message: ''
  };

  /* —————————————————————————————————————————— */
  /* ——————————————— useEffect  ——————————————— */
  /* —————————————————————————————————————————— */


  /* 최초 접근 시 */
  useEffect(() => {

    // 최초 접근 시 온보딩 노출
    if (!firstAccess.access) {
      // 온보딩 UI 노출 시키기
      setOnBoarding(true);
      // 온보딩 UI 노출 이후
      setFirstAccess({ access: true, login: false });
    }

    // 회원탈퇴 상태 초기화 -> 탈퇴 후 로그인 창 진입 시
    resetWithdraw();
    resetRegisterDetail();

    // 로컬에 저장된 아이디가 있을 경우
    if (localSaveId !== '') {
      setInputs({ ...inputs, user_id: localSaveId });
      setIdSave(true);
    }

    // 자동로그아웃 팝업 초기화
    setPopupAuto((prevData) => ({ ...prevData, TYPE: '' }));

    // 첫번째 로그인 초기화
    setFirstLogin(false);
    setPopupTutorial({ TYPE: '' });
  }, []);

  /* 중복로그인으로 해당화면으로 온 경우 */
  useEffect(() => {
    if (forceLogout.force) {
      if (forceLogout.reason === 'otherDevice') {
        setPopup({ TYPE: 'alert', TITLE: 'Notification', CONTENT: 'You have been logged out of this device \n due to login on another device with the same ID.', BUTTON: 'OK', RESERVE: '' });
      }
      else if (forceLogout.reason === 'session') {
        setPopup({ TYPE: 'alert', TITLE: 'Notification', CONTENT: 'You will be automatically logged out \n due to inactivity for a certain period.', BUTTON: 'OK', RESERVE: '' });
      }
      else if (forceLogout.reason === 'token') {
        setPopup({ TYPE: 'alert', TITLE: 'Notification', CONTENT: 'You will be automatically logged out \n due to inactivity for a certain period.', BUTTON: 'OK', RESERVE: '' });
      }
      setForceLogout({ force: false });
    }
  }, [forceLogout]);

  /* 2차인증 후 */
  useEffect(() => {
    if (verifyInfo?.finish) {
      const uname = verifyInfo.username;
      const apiRes = verifyInfo.apiRes;
      setVerifyInfo(null);
      afterTryLogin(uname, apiRes, 2);
    }
  }, [verifyInfo]);

  /* OAuth Redirect ( none popup ) */
  useEffect(() => {
    let codeParam = searchParams.get('code');
    if (codeParam) {

      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.delete('code');
      updatedSearchParams.delete('scope');
      updatedSearchParams.delete('authuser');
      updatedSearchParams.delete('prompt');
      setSearchParams(updatedSearchParams);

      if (registerDetail?.step1?.oauthType) {
        oauthLogin(registerDetail.step1.oauthType, codeParam);
      }
    }
  }, [searchParams]);

  /* Window callback */
  useEffect(() => {
    if (externalPopupInfo.callbackData?.oAuthInfo) {
      oauthLogin(registerDetail.step1.oauthType!, externalPopupInfo.callbackData.oAuthInfo.code);
    }
  }, [externalPopupInfo]);




  /* —————————————————————————————————————————— */
  /* ———————————————— API 함수  ———————————————— */
  /* —————————————————————————————————————————— */

  /* Login || VcAuth 이후 프로세스 */
  const afterTryLogin = async (username: string, loginApiRes: any, callType: number): Promise<void> => {

    if (loginApiRes.message === 'LOCK') {
      setPopup({ TYPE: 'alert', TITLE: 'Notification', CONTENT: 'You have entered the password incorrectly more than 5 times,\n and your account has been locked.\n Please reset your password.', BUTTON: 'OK', RESERVE: '' });
      delayLogBody.message = '비밀번호 5회 이상 잘못 입력';
    }
    else if (loginApiRes.message === 'BLOCKED') {
      setPopup({ TYPE: 'alert', TITLE: 'Notification', CONTENT: 'Your account is locked.\n Please contact the administrator.', BUTTON: 'OK', RESERVE: '' });
      delayLogBody.message = '잠금처리 된 계정';
    }
    else if (loginApiRes.message === 'PASSWORD') {
      setUserTokenInfo({ coreAccessToken: loginApiRes.accessToken });

      delayLogBody.message = '비밀번호 만료로 인한 페이지 이동';
      delayLogBody.endAt = new Date().toISOString();
      delayLogBody.millisecond = `${new Date(delayLogBody.endAt).getTime() - new Date(delayLogBody.startAt).getTime()}`;
      ncpLogUpdate({ logLevel: 'DEBUG', body: delayLogBody });

      if (idSave) {
        setLocalSaveId(inputs.user_id);
      } else {
        setLocalSaveId('');
      }

      navigate('/auth/changePw', { replace: true, state: { username: loginApiRes.data.username, callbackId: loginApiRes.data.callbackId } });
      return;
    }
    else if (loginApiRes.message === 'RESET_PASSWORD') {
      setUserTokenInfo({ coreAccessToken: loginApiRes.accessToken });

      delayLogBody.message = '비밀번호 초기화로인해 변경페이지 이동';
      delayLogBody.endAt = new Date().toISOString();
      delayLogBody.millisecond = `${new Date(delayLogBody.endAt).getTime() - new Date(delayLogBody.startAt).getTime()}`;
      ncpLogUpdate({ logLevel: 'DEBUG', body: delayLogBody });

      if (idSave) {
        setLocalSaveId(inputs.user_id);
      } else {
        setLocalSaveId('');
      }

      navigate('/auth/findPw', { replace: true });
      return;
    }
    else if (loginApiRes.message === 'VERIFY') {

      /* 2차인증 코드발급 */
      let rcApiRes: rcFunctionRes;
      if (callType > 2) {
        rcApiRes = await requestCode({ type: 'EMAIL', checkType: 'make', email: loginApiRes.email });
      } else {
        rcApiRes = await requestCode({ type: 'PHONE', checkType: 'make', username: inputs.user_id });
      }
      if (rcApiRes.message === 'SUCCESS' && rcApiRes.data) {

        delayLogBody.message = '2차인증 성공';

        // 2차인증 받는 사용자 정보
        setVerifyInfo({ username: username, requestId: rcApiRes.data.requestId, finish: false });
        // 로그인 추가인증이 필요한 경우
        setPopup({ TYPE: 'phoneCert', TITLE: 'Additional Authentication for Login', CONTENT: rcApiRes.data.requestedTo, BUTTON: '', RESERVE: '' });
      } else if (rcApiRes.message === 'TOO_MANY') {

        delayLogBody.message = '많은 요청으로 인한 2차인증 실패';

        setPopup({ TYPE: 'alert', TITLE: 'Notification', CONTENT: 'There are too many requests.\n Please try again later.', BUTTON: 'OK', RESERVE: '' });
      }
      else if (rcApiRes.message === 'TIMEOUT') {
        navigate('/auth/error');
      }
      else {

        delayLogBody.message = '인증코드 발급 실패';

        // 서버 에러 팝업
        setPopupError(prevData => [...prevData, { CODE: rcApiRes.errorCode, MESSAGE: rcApiRes.errorMessage }]);
        // setPopup({ TYPE: 'alert', TITLE: '알림', CONTENT: '인증코드 발급에 실패했습니다.\n잠시 후 다시 시도해 주세요.', BUTTON: '확인', RESERVE: '' });


      }

    }
    else if (loginApiRes.message === 'ALREADY' || loginApiRes.message === 'SUCCESS') {

      setLoginSessionCount(publicMethod.sessionUpdate);

      // 선생님, 학생 구분


      const role = loginApiRes.data.baseInfo.role;


      if (role !== 'teacher' && role !== 'student') {
        if (callType < 3) {
          setIdBorderError(true);
          setPwBorderError(true);
          setIdRefresh(true);
          setLoginFail(true);
        }

        delayLogBody.message = 'role 정보 오류';
        delayLogBody.endAt = new Date().toISOString();
        delayLogBody.millisecond = `${new Date(delayLogBody.endAt).getTime() - new Date(delayLogBody.startAt).getTime()}`;
        ncpLogUpdate({ logLevel: 'DEBUG', body: delayLogBody });
        return;
      }

      /* 사용자 정보 저장 */
      setUserTokenInfo({ coreAccessToken: loginApiRes.accessToken });
      setUserBaseInfo(loginApiRes.data);

      /* 선생님 관리 데이터 셋팅 */
      if (role === 'teacher' && loginApiRes.data.teacherClassSetting) {

        try {
          const managementData: { groupSetting?: any, AIoTSetting?: any } = JSON.parse(loginApiRes.data.teacherClassSetting);

          if (managementData?.AIoTSetting[loginApiRes.data.baseInfo.userId]) {
            const manageData: any = JSON.parse(managementData.AIoTSetting[loginApiRes.data.baseInfo.userId]);
            if (`${process.env.REACT_APP_ENV}` !== 'PRODUCTION') {
              console.log('디바이스정보', manageData);
            }
            setDeviceManagement(manageData);
          }

        } catch (e: any) {
          console.log(e);
          if (`${process.env.REACT_APP_ENV}` !== 'PRODUCTION') {
            console.log(`LINK SCHOOL MANAGEMENT DATA ERR : ${loginApiRes.data.teacherClassSetting}`);
          }
        }
      }

      // 로그인된 기기가 따로 존재할 경우
      if (idSave) {
        setLocalSaveId(inputs.user_id);
      } else {
        setLocalSaveId('');
      }
      if (loginApiRes.message === 'ALREADY') {
        setPopup({ TYPE: 'alertLogin', TITLE: 'Notification', CONTENT: 'There is another device logged in with the same ID.\n You will log in with the current device.', BUTTON: 'OK', RESERVE: role });
      }
      else if (loginApiRes.message === 'SUCCESS') {

        delayLogBody.message = '로그인 성공';
        delayLogBody.endAt = new Date().toISOString();
        delayLogBody.millisecond = `${new Date(delayLogBody.endAt).getTime() - new Date(delayLogBody.startAt).getTime()}`;
        ncpLogUpdate({ logLevel: 'DEBUG', body: delayLogBody });

        if (role === 'teacher') {
          navigate('/service/teacher/home', { replace: true });
        } else if (role === 'student') {
          navigate('/service/student/home', { replace: true });
        }
        return;
      }
    }
    else if (loginApiRes.message === 'TIMEOUT') {
      navigate('/auth/error');
    }
    else {
      // 서버 에러 팝업
      setPopupError(prevData => [...prevData, { CODE: loginApiRes.errorCode, MESSAGE: loginApiRes.errorMessage }]);

      delayLogBody.message = '로그인 실패';

      if (callType < 3) {
        setIdBorderError(true);
        setPwBorderError(true);
        setIdRefresh(true);
        setLoginFail(true);
      }
    }


    // 로그인 버튼 활성화
    setLoginClickable(true);

    delayLogBody.endAt = new Date().toISOString();
    delayLogBody.millisecond = `${new Date(delayLogBody.endAt).getTime() - new Date(delayLogBody.startAt).getTime()}`;
    ncpLogUpdate({ logLevel: 'DEBUG', body: delayLogBody });
  };

  /* oauth login */
  const oauthLogin = async (oauthType: string, code?: string) => {

    // 로그인 버튼 활성화일 경우에만
    if (loginClickable) {

      if (code) {

        setExternalPopupInfo({ externalPopup: false });

        // 로그인 버튼 비활성화
        setLoginClickable(false);

        delayLogBody.startAt = new Date().toISOString();
        let oAuthRes: googleFunctionRes | whaleFunctionRes;
        if (oauthType === 'google') {
          oAuthRes = await googleOAuth({ code: code });
        }
        else {
          oAuthRes = await whaleOAuth({ code: code });
        }
        if (oAuthRes) {
          if (oAuthRes.message === 'NOT_FOUND') {

            // 로그인 버튼 활성화 (로딩 제거)
            setLoginClickable(true);

            const OAUTH_SSO_TOKEN = oAuthRes.ssoToken;
            const OAUTH_EMAIL = oAuthRes.email;


            setRegisterDetail(prevRegisterDetail => ({
              ...prevRegisterDetail,
              step1: { ...prevRegisterDetail.step1, oauthType: oauthType, accessToken: OAUTH_SSO_TOKEN! },
              step4: { ...prevRegisterDetail.step4, email: OAUTH_EMAIL! }
            }));

            navigate('/auth/registerUserType');
          }
          else if (oAuthRes.message === 'TIMEOUT') {
            navigate('/auth/error');
          }
          else {
            await afterTryLogin(oauthType, oAuthRes, oauthType === 'google' ? 4 : 3);
          }
        }
      } else {

        setRegisterDetail(prevRegisterDetail => ({
          ...prevRegisterDetail,
          step1: { ...prevRegisterDetail.step1, oauthType: oauthType }
        }));

        let endPoint = '';
        if (oauthType === 'google') {
          // endPoint = 'https://accounts.google.com/o/oauth2/v2/auth?response_type=code';
          // endPoint += `&client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}`;
          // endPoint += `&redirect_uri=${encodeURIComponent(process.env.REACT_APP_BASEURL!)}`;
          // endPoint += '&scope=email profile';
          endPoint += 'https://api.a-school.org/api/core/v1/auth/google'

        }
        else {
          endPoint = 'https://auth.whalespace.io/oauth2/v1.1/authorize?response_type=code';
          endPoint += `&client_id=${process.env.REACT_APP_WHALESPACE_CLIENT_ID}`;
          endPoint += `&redirect_uri=${encodeURIComponent(process.env.REACT_APP_BASEURL!)}`;
          endPoint += `&state=${encodeURIComponent('reactApp')}`;
        }
        if (deviceInfo?.model === 'WinPC') {
          window.MirrorPopupAuth(endPoint);
        } else {
          window.location.href = endPoint;
        }

      }
    }

  };

  /* —————————————————————————————————————————— */
  /* ————————————— User Action 함수 ———————————— */
  /* —————————————————————————————————————————— */

  /* 로그인 하기 */
  const runLogin = async () => {

    delayLogBody.startAt = new Date().toISOString();

    // 입력값/에러표기 초기화
    resetValidation();

    // 아이디, 비밀번호 미입력시
    if (inputs.user_id === '' || inputs.user_pw === '') {
      if (inputs.user_id === '') {
        setIdBorderError(true);
        setIdBlank(true);
      }

      if (inputs.user_pw === '') {
        setPwBorderError(true);
        setPwBlank(true);
      }
    }
    // 아이디, 비밀번호 입력시 로그인 API 실행
    else if (loginClickable) {
      // 로그인 버튼 비활성화
      setLoginClickable(false);
      /* Login Api */
      const loginApiRes: loginFunctionRes = await login({ username: inputs.user_id, password: inputs.user_pw, os: deviceInfo?.osInfo! });



      await afterTryLogin(inputs.user_id, loginApiRes, 1);
    }

  };

  /* 링스쿨 시작 버튼 클릭 시 */
  const startLinkSchool = () => {
    setOnBoarding(false);
  };

  /* ID 입력값 삭제 */
  const idInputDelete = () => {
    setInputs((prevData) => ({ ...prevData, user_id: '' }));
  };

  /* 입력값 설정 */
  const handleInputs = (event: React.ChangeEvent<HTMLInputElement>) => {

    const { name, value } = event.target;

    // 영문, 한글, 숫자, 특수문자 입력 가능 (공백 입력 불가)
    const isValidIdInput = /^[a-zA-Z0-9ㄱ-ㅎㅏ-ㅣ가-힣!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/.test(value);
    const isValidPwInput = /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/.test(value);
    if (name === 'user_id') {
      if (value.length <= 15 && isValidIdInput) { // 입력값이 15자 이하이고, 위 검사값에 부합할 경우에만 상태 업데이트
        setInputs((prevData) => ({ ...prevData, [name]: value }));
      }
    } else if (name === 'user_pw') {

      if (value.length <= 20 && isValidPwInput) { // 입력값이 20자 이하이고, 위 검사값에 부합할 경우에만 상태 업데이트
        setInputs((prevData) => ({ ...prevData, [name]: value }));
      }
    }
  };

  /* 엔터키 클릭 시 로그인 */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (popup.TYPE === '') { // 팝업이 떠있지 않을때만 엔터키 클릭 허용
        runLogin();
      } else {
        console.log('팝업 노출 -> 엔터키 방지');
      }

    }
  };



  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.getModifierState('CapsLock')) {
      setCapsLockOn(true);
    } else {
      setCapsLockOn(false);
    }
  };


  /* —————————————————————————————————————————— */
  /* ———————————————— 기타 함수  ———————————————— */
  /* —————————————————————————————————————————— */

  /* 입력값/에러표기 초기화 */
  const resetValidation = () => {
    setIdBorderError(false);
    setPwBorderError(false);
    setIdRefresh(false);
    setIdBlank(false);
    setPwBlank(false);
    setCapsLockOn(false);
    setLoginFail(false);
  };


  const handleMouseDown = (e: any) => {
    setDragging(true);
    setStartX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragging || !sliderRef.current) return;
    const moveX = e.clientX - startX;

    if (moveX > 50) {
      // 이전 슬라이드로 이동
      sliderRef.current.slickPrev();
      setDragging(false);
    } else if (moveX < -50) {
      // 다음 슬라이드로 이동
      sliderRef.current.slickNext();
      setDragging(false);
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  // 터치 이벤트 핸들러 추가
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setDragging(true);
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragging || !sliderRef.current) return;
    const moveX = e.touches[0].clientX - startX;

    if (moveX > 50) {
      // 이전 슬라이드로 이동
      sliderRef.current.slickPrev();
      setDragging(false);
    } else if (moveX < -50) {
      // 다음 슬라이드로 이동
      sliderRef.current.slickNext();
      setDragging(false);
    }
  };

  const handleTouchEnd = () => {
    setDragging(false);
  };


  return {
    inputs, idRefresh, passwordHidden, setPasswordHidden, idBorderError, pwBorderError, idBlank, pwBlank, capsLockOn, loginFail, idSave, setIdSave, handleKeyUp,
    runLogin, handleKeyPress, idInputDelete, handleInputs, navigate, oauthLogin, onBoarding, setOnBoarding, onBoardingData, currentSlide, onBoardingSlideSettings, startLinkSchool, loginClickable,
    handleMouseDown, handleMouseMove, handleMouseUp, sliderRef, handleTouchStart, handleTouchMove, handleTouchEnd, goPreviousSlide, goNextSlide, setPopup
  };

};
